/**
 * Static property-catalog fallback for old emulators.
 *
 * Old AAOS emulators have no `vhal_proxy_server`; ctrl drives them over an
 * ADB fallback that serves CheckHealth/GetValues/SetValues but NOT
 * `getAllPropertyConfig`. Without a live config catalog the Vehicle Controls
 * panel would render nothing. We synthesise the catalog from the bundled
 * `VHAL_PROPERTY_DATABASE` instead — live values still flow through
 * getValues/setValues.
 */

import {
  getAreaType,
  getPropertyType,
  VhalAreaType,
  VhalPropertyAccess,
  VhalPropertyChangeMode,
  VHAL_PROPERTY_DATABASE,
  type VhalPropertyConfig,
} from "../types/vhal";

/**
 * Candidate area IDs to probe per zoned area type. The static catalog carries
 * no per-property area metadata, so we enumerate the standard AOSP areas the
 * Vehicle Controls panel reads/writes (mirrors the literals already used in
 * `VhalAreaId` and `VehicleControlsPanel.readCurrentValues`). GLOBAL props are
 * omitted — they default to areaId 0.
 */
const AREA_IDS_BY_TYPE: Partial<Record<VhalAreaType, number[]>> = {
  [VhalAreaType.SEAT]: [0x1, 0x2, 0x4, 0x10, 0x20, 0x40],
  [VhalAreaType.DOOR]: [0x1, 0x4, 0x10, 0x40],
  [VhalAreaType.WINDOW]: [0x1, 0x2, 0x10, 0x40, 0x100, 0x400],
  [VhalAreaType.WHEEL]: [0x1, 0x2, 0x4, 0x8],
  [VhalAreaType.MIRROR]: [0x1, 0x2, 0x4],
};

/**
 * Build a `VhalPropertyConfig[]` from the bundled static catalog. Used when
 * live `getAllPropertyConfig` is unavailable (old emulators).
 */
export function staticCatalogConfigs(): VhalPropertyConfig[] {
  return Object.entries(VHAL_PROPERTY_DATABASE).map(([id, info]) => {
    const propertyId = Number(id);
    const areaIds = AREA_IDS_BY_TYPE[getAreaType(propertyId)];
    const cfg: VhalPropertyConfig = {
      propertyId,
      access: info.readOnly
        ? VhalPropertyAccess.READ
        : VhalPropertyAccess.READ_WRITE,
      changeMode: VhalPropertyChangeMode.ON_CHANGE,
      propertyType: getPropertyType(propertyId),
    };
    if (info.name) cfg.name = info.name;
    if (areaIds) cfg.areaConfigs = areaIds.map((areaId) => ({ areaId }));
    return cfg;
  });
}
